import { Box } from "@twilio-paste/core/box";
import { Combobox, UseComboboxStateChange } from "@twilio-paste/core/combobox";
import { Separator } from "@twilio-paste/core/separator";
import { SkeletonLoader } from "@twilio-paste/core/skeleton-loader";
import React from "react";
import { VERSION } from "../../../version";
import { NPM_FLEX_UI } from "../../constants";

const Text = {
    oneRelease: "1.x Releases",
    twoRelease: "2.x Releases"
};

const VersionSwitcher = () => {
    const [loaded, setLoaded] = React.useState(false);
    const [selectedVersion, setSelectedVersion] = React.useState({ label: "", type: "" });
    const [versions, setVersions] = React.useState<{ label: string; type: string }[]>([]);
    React.useEffect(() => {
        setSelectedVersion({ type: VERSION.startsWith("2.") ? Text.twoRelease : Text.oneRelease, label: VERSION });
        if (!loaded) {
            getData();
        }
    }, []);

    const getData = async () => {
        // get data from GitHub api
        const data = await fetch(NPM_FLEX_UI, {
            method: "GET",
            headers: {
                Accept: "application/vnd.npm.install-v1+json"
            }
        });
        const parsedData = await data.json();
        const availableVersions = Object.keys(parsedData.versions).filter(
            (val) => !(val.startsWith("0.") || val.includes("-rc"))
        );
        if (!availableVersions.find((val) => val === VERSION)) {
            availableVersions.push(VERSION);
        }

        const version1 = availableVersions
            .filter((val) => val.startsWith("1."))
            .map((a) =>
                a
                    .split(".")
                    .map((n) => +n + 100000)
                    .join(".")
            )
            .sort((a, b) => a.localeCompare(b))
            .map((a) =>
                a
                    .split(".")
                    .map((n) => +n - 100000)
                    .join(".")
            );
        // Implement sorting post GA
        const version2 = availableVersions.filter((val) => val.startsWith("2.")).sort((a, b) => a.localeCompare(b));
        setVersions(
            [...version1, ...version2]
                .map((val) => ({ label: val, type: val.startsWith("2.") ? Text.twoRelease : Text.oneRelease }))
                .reverse()
        );
        setLoaded(true);
    };

    const versionChange = (event: UseComboboxStateChange<any>) => {
        if (window.location.hostname === "localhost") {
            // Do nothing on localhost to prevent page crash
            console.log(`If this was prod, you would be redirected to version ${event.selectedItem.label}.`);
        } else {
            const url = window.location.href.split("/flex-ui/");
            const [version, ...prefix] = url[1].split("/");
            window.location.href = `${url[0]}/flex-ui/${event.selectedItem.label}/${prefix.join("/") || ""}`;
        }
    };

    return (
        <Box as="div" paddingLeft="space70" paddingRight="space70">
            {loaded && (
                <>
                    <Combobox
                        groupItemsBy="type"
                        items={versions}
                        labelText="Version"
                        data-testid="version-switcher"
                        selectedItem={selectedVersion}
                        optionTemplate={(item) => <div>{item.label}</div>}
                        onSelectedItemChange={versionChange}
                        itemToString={(item) => (item ? item.label : null)}
                    />
                    <Separator orientation="horizontal" verticalSpacing="space70" />
                </>
            )}
            {!loaded && (
                <>
                    <SkeletonLoader height="60px" />
                    <Separator orientation="horizontal" verticalSpacing="space70" />
                </>
            )}
        </Box>
    );
};

export { VersionSwitcher };
